import type { RouteLocationNormalized } from 'vue-router'
import { useUserStore } from '@/config/store/userStore'
import { isModuleActive } from '@/services/application'
import { hasAnyTrainingSettingsAccess, hasTrainingSettingAccess, type TrainingSettingAccessKey } from '@/services/training/settingsAccess'
import { canUserAccessModule as canUserAccessEvaluationModule } from '@/config/router/navigationsGuards/guardEvaluation'

const moduleId = 9

export const canUserAccessModule = (user) => {
  const isFirmAccess = isModuleActive(user.firm, moduleId)
  return isFirmAccess && user.profile.modules.training?.isAccess
}

export const guardTrainingAccess = () => {
  const userStore = useUserStore()
  const user = userStore.user
  const canAccess = canUserAccessModule(user)
  return canAccess || { name: 'Home' }
}

export const guardCreateTraining = () => {
  const userStore = useUserStore()
  const user = userStore.user
  return user.profile.modules.training?.createTrainingAccess ? user.profile.modules.training.createTrainingAccess : { name: 'Home' }
}

export const guardTrainingCatalog = () => {
  const userStore = useUserStore()
  const user = userStore.user
  return user.profile.modules.training?.catalogAccess ? user.profile.modules.training.catalogAccess : { name: 'Home' }
}

export const guardMyTraining = () => {
  const userStore = useUserStore()
  const user = userStore.user
  return user.profile.modules.training?.myTrainingsAccess ? user.profile.modules.training.myTrainingsAccess : { name: 'Home' }
}

export const guardTrainingStats = () => {
  const userStore = useUserStore()
  const user = userStore.user
  return user.profile.modules.training?.statisticsAccess ? user.profile.modules.training?.statisticsAccess : { name: 'Home' }
}

export const guardTrainingBudget = () => {
  const userStore = useUserStore()
  const user = userStore.user
  return user.profile.modules.training?.budgetAccess
    ? user.profile.modules.training.budgetAccess
    : { name: 'Home' }
}

export const guardTrainingWishesAttribution = () => {
  const userStore = useUserStore()
  const user = userStore.user
  const hasWishesAccess = !!user.profile.modules.training?.wishesAttributionAccess
  const hasEvaluationAccess = canUserAccessEvaluationModule(user)
  return (hasWishesAccess && hasEvaluationAccess) || { name: 'Home' }
}

export const guardElearningCatalog = () => {
  const userStore = useUserStore()
  const user = userStore.user
  return user.profile.modules.training?.elearningCatalogAccess ? user.profile.modules.training?.elearningCatalogAccess : { name: 'Home' }
}

// Following an assigned module (by hand or by a learning path) only needs "my trainings": the API serves the
// learner their own assignments only.
export const guardElearningAssignmentPlayer = () => {
  const userStore = useUserStore()
  const training = userStore.user.profile.modules.training
  return (training?.elearningCatalogAccess || training?.myTrainingsAccess) ? true : { name: 'Home' }
}

export const guardElearningBuilder = () => {
  const userStore = useUserStore()
  const user = userStore.user
  const training = user.profile.modules.training
  return (training?.elearningBuilderAccess || training?.canEditElearning)
    ? true
    : { name: 'Home' }
}

export const guardElearningAccess = () => {
  const catalog = guardElearningCatalog()
  if (catalog === true)
    return true
  return guardElearningBuilder()
}

export const guardElearningAssignmentAdmin = () => {
  const userStore = useUserStore()
  const user = userStore.user
  const training = user.profile.modules.training
  return (training?.elearningAssignmentAccess || training?.elearningBuilderAccess || training?.canEditElearning)
    ? true
    : { name: 'Home' }
}

/**
 * Space of one e-learning course: its content and settings for course editors, its participants
 * for them and for the managers of assignments, who land on that tab.
 */
export const guardElearningCourseWorkspace = (to: RouteLocationNormalized) => {
  const training = useUserStore().user.profile.modules.training
  const canEdit = !!(training?.elearningBuilderAccess || training?.canEditElearning)
  const canManageParticipants = canEdit || !!training?.elearningAssignmentAccess
  if (to.params.tab === 'participants')
    return canManageParticipants ? true : { name: 'Home' }
  if (canEdit)
    return true
  if (canManageParticipants && to.params.courseId !== 'new')
    return { name: 'ElearningCourseEdit', params: { courseId: to.params.courseId, tab: 'participants' } }
  return { name: 'Home' }
}

export const guardLearningPath = () => {
  const userStore = useUserStore()
  const user = userStore.user
  return user.profile.modules.training?.learningPathAccess ? user.profile.modules.training?.learningPathAccess : { name: 'Home' }
}

export const guardElearningStats = () => {
  const userStore = useUserStore()
  const user = userStore.user
  return user.profile.modules.training?.elearningStatisticsAccess ? user.profile.modules.training?.elearningStatisticsAccess : { name: 'Home' }
}

export const guardTrainingEdit = () => {
  const userStore = useUserStore()
  const user = userStore.user
  return user.profile.modules.training?.canEditTraining ? user.profile.modules.training?.canEditTraining : { name: 'Home' }
}

export const guardOrganizeSession = () => {
  const userStore = useUserStore()
  const user = userStore.user
  return user.profile.modules.training?.canOrganizeSession ? user.profile.modules.training?.canOrganizeSession : { name: 'Home' }
}

export const guardTrainingSettingsAccess = () => {
  const userStore = useUserStore()
  const user = userStore.user
  return hasAnyTrainingSettingsAccess(user.profile.modules.training) || { name: 'Home' }
}

export const guardTrainingSettingAccess = (key: TrainingSettingAccessKey) => {
  const userStore = useUserStore()
  const user = userStore.user
  return hasTrainingSettingAccess(user.profile.modules.training, key) || { name: 'Home' }
}

export const guardTrainingEvaluationFormsSettingsAccess = () =>
  guardTrainingSettingAccess('settingsEvaluationFormsAccess')
